'use client';

import { ChevronRight, Store } from 'lucide-react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';

export type MultilocalLocation = {
  id: string;
  name: string;
  title: string | null;
  logo: string | null;
  banner: string | null;
  slug: string;
  square_image?: string | null;
  banner_image?: string | null;
};

interface Props {
  groupName: string;
  locations: MultilocalLocation[];
  layout?: 'threeColumn' | 'twoColumn' | string;
  onSelectLocation: (location: MultilocalLocation) => void;
}

export function MultilocalLocationSelector({ groupName, locations, layout = 'threeColumn', onSelectLocation }: Props) {
  const isVidriera = layout === 'twoColumn';

  return (
    <div className="flex h-full w-full flex-col items-center justify-between bg-zinc-950 text-white p-6 lg:p-12 overflow-y-auto">
      {/* Header */}
      <div className="flex flex-col items-center text-center mt-4 max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-2 flex items-center gap-1.5">
          <Store className="size-4" /> Patio de Comidas
        </span>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3">{groupName}</h1>
        <p className="text-zinc-400 text-base md:text-lg">Selecciona tu local favorito para comenzar tu pedido</p>
      </div>

      {/* Mosaico: Modo VIDRIERA (Carrusel / Slider Horizontal con Tarjetas Banner) */}
      {isVidriera ? (
        <div className="w-full max-w-6xl my-8 flex items-center overflow-x-auto gap-6 pb-6 pt-2 scrollbar-thin scrollbar-thumb-zinc-700">
          {locations.map(loc => {
            const bannerImage = loc.banner_image || loc.banner;
            return (
              <button
                key={loc.id}
                onClick={() => onSelectLocation(loc)}
                type="button"
                className="group relative flex-none w-80 md:w-96 flex flex-col items-start justify-between rounded-3xl bg-linear-to-b from-zinc-900 to-zinc-900/90 border border-zinc-800 p-7 text-left transition-all duration-300 hover:scale-[1.03] hover:border-orange-500/60 hover:shadow-2xl hover:shadow-orange-500/20 focus:outline-hidden"
              >
                <div className="w-full flex items-center justify-between mb-6">
                  <div className="relative h-24 w-full overflow-hidden rounded-2xl bg-zinc-800/90 flex items-center justify-center border border-zinc-700/60 shadow-md">
                    {bannerImage ? (
                      <Image src={bannerImage} alt={loc.name} fill className="object-cover" sizes="(max-width: 768px) 320px, 384px" />
                    ) : (
                      <span className="text-3xl font-black text-orange-500">{loc.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                </div>

                <div className="w-full space-y-2 mb-6">
                  <h2 className="text-2xl font-bold text-white group-hover:text-orange-400 transition-colors">
                    {loc.name}
                  </h2>
                  {loc.title && <p className="text-xs text-zinc-400 line-clamp-2 leading-relaxed">{loc.title}</p>}
                </div>

                <div className="w-full">
                  <Button
                    variant="secondary"
                    className="w-full justify-between bg-orange-500 text-white hover:bg-orange-600 font-bold py-6 text-base rounded-xl shadow-lg transition-all"
                  >
                    <span>Ingresar a {loc.name}</span>
                    <ChevronRight className="size-5" />
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* Mosaico: Modo GRILLA (Cuadrícula Responsiva) */
        <div className="w-full max-w-5xl my-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {locations.map(loc => {
            const squareImage = loc.square_image || loc.logo;
            return (
              <button
                key={loc.id}
                onClick={() => onSelectLocation(loc)}
                type="button"
                className="group relative flex flex-col items-center justify-between rounded-2xl bg-zinc-900/80 border border-zinc-800 p-6 text-center transition-all duration-200 hover:scale-[1.02] hover:border-orange-500/50 hover:bg-zinc-900 hover:shadow-xl hover:shadow-orange-500/10 focus:outline-hidden"
              >
                <div className="relative h-28 w-28 mb-4 overflow-hidden rounded-full bg-zinc-800 flex items-center justify-center border border-zinc-700/50 group-hover:border-orange-500/30">
                  {squareImage ? (
                    <Image src={squareImage} alt={loc.name} fill className="object-cover" sizes="112px" />
                  ) : (
                    <span className="text-3xl font-black text-orange-500">{loc.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>

                <div className="w-full space-y-1">
                  <h2 className="text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                    {loc.name}
                  </h2>
                  {loc.title && <p className="text-xs text-zinc-400 line-clamp-2">{loc.title}</p>}
                </div>

                <div className="mt-6 w-full">
                  <Button
                    variant="secondary"
                    className="w-full bg-orange-500/10 text-orange-400 border border-orange-500/20 group-hover:bg-orange-500 group-hover:text-white transition-all font-semibold"
                  >
                    Hacer Pedido →
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="mb-4 text-center text-xs text-zinc-500">
        <p>OrderFast • Sistema de Tótems Autogestión</p>
      </div>
    </div>
  );
}
